import type { PaginationType } from '../types/Pagination.types';
import z from 'zod';
import { CategoryDtoSchema } from '../categories/categories.schema';
import { GameAssetDtoSchema, MediaDtoSchema } from '../media/media.schema';
import { ProviderDtoSchema } from '../providers/providers.schema';
import { PageContentDtoSchema } from '../system-pages';
import { createUpdateZodSchema } from '../types/DateUtils.types';
import { PaginationParamsSchema } from '../types/Pagination.types';
import {
  GAME_BET_LEVEL_OPTIONS,
  GAME_JACKPOT_TYPE_OPTIONS,
  GAME_PROMO_TOOL_OPTIONS,
  GAME_RTP_TYPE_OPTIONS,
  GAME_SUB_JACKPOT_TYPE_OPTIONS,
  GAME_TOP_PRIZE_OPTIONS,
  GAME_VOLATILITY_OPTIONS,
} from './game-details.enum';

export const GameRtpSchema = z.preprocess(
  value => value === null
    ? undefined
    : typeof value === 'number'
      ? String(value)
      : value,
  z.string().optional(),
);

export const PartnerDtoSchema = z.object({
  id: z.string().uuid(),
  partnerId: z.string(),
  partnerCode: z.string(),
  isActive: z.boolean(),
}).merge(createUpdateZodSchema);

export type Partner = z.infer<typeof PartnerDtoSchema>;

export const GameDetailsDtoSchema = z.object({
  category: z.array(z.string()).optional(),
  subCategory: z.array(z.string()).optional(),
  volatility: z.array(z.enum(GAME_VOLATILITY_OPTIONS)).optional(),
  rtpType: z.enum(GAME_RTP_TYPE_OPTIONS).optional(),
  rtp: z.number().nullable().optional(),
  topPrize: z.enum(GAME_TOP_PRIZE_OPTIONS).optional(),
  features: z.array(z.string()).optional(),
  subFeature: z.array(z.string()).optional(),
  gameMechanics: z.array(z.string()).optional(),
  subGameMechanics: z.array(z.string()).optional(),
  subSubGameMechanics: z.array(z.string()).optional(),
  grid: z.array(z.string()).optional(),
  themes: z.array(z.string()).optional(),
  subThemes: z.array(z.string()).optional(),
  minBet: z.enum(GAME_BET_LEVEL_OPTIONS).optional(),
  maxBet: z.enum(GAME_BET_LEVEL_OPTIONS).optional(),
  promoTools: z.array(z.enum(GAME_PROMO_TOOL_OPTIONS)).optional(),
  jackpotType: z.enum(GAME_JACKPOT_TYPE_OPTIONS).optional(),
  subJackpotType: z.enum(GAME_SUB_JACKPOT_TYPE_OPTIONS).optional(),
}).describe('GameDetailsDtoSchema');

export const GameDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string()
    .nullable(),
  gameId: z.string(), // RELAX id, we will not use it

  alias: z.string(), // alias, slug for url
  order: z.number(),

  isActive: z.boolean(),
  isBonusAllowed: z.boolean().optional().default(true),
  cover: MediaDtoSchema.nullable(),
  settings: z.object({
    allow: z.string().optional(),
  }).nullable(),
  categories: z.array(CategoryDtoSchema),
  provider: ProviderDtoSchema.nullish(),
  partner: PartnerDtoSchema.nullish(),
  isFavorite: z.boolean().optional(),
  // field for seo game gallery
  assets: z.array(GameAssetDtoSchema).nullish(),

  pageContent: PageContentDtoSchema.nullish(),

  details: GameDetailsDtoSchema.optional().nullable(),

}).merge(createUpdateZodSchema).describe('GameDtoSchema');

export const GameListItemDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  alias: z.string(),
  cover: MediaDtoSchema.nullish().transform(v => v ?? null),
  isFavourite: z.boolean().optional().default(false),
}).describe('GameListItemDtoSchema');

export const GameLaunchUrlPayloadSchema = z.object({
  gameId: z.string(),
  userId: z.string(),
  currency: z.string(),
  language: z.string(),
  country: z.string(),
  device: z.string(),
});

export const GameLaunchUrlResponseSchema = z.object({
  code: z.string(),
  url: z.string(),
});

export const GamesFiltersSchema = z.object({
  name: z.string().optional(),
  gameId: z.string().optional(),
  gameCode: z.string().optional(),
  alias: z.string().optional(),
  isActive: z.boolean().optional(),
  categoryId: z.string().optional(),
  partnerId: z.string().optional(),
  providerId: z.union([
    z.string(),
    z.object({
      $in: z.array(z.string()).optional(),
    }),
  ]).optional(),
}).optional();

export const GamesParamsSchema = PaginationParamsSchema(GamesFiltersSchema);

export type GamesFilters = z.infer<typeof GamesFiltersSchema>;
export type GamesFiltersParamsPayload = z.infer<typeof GamesParamsSchema>;

const RoundVerificationSchema = z.object({
  id: z.string().uuid(),
  state: z.enum(['pending', 'success', 'failed', 'processing']),
  transactionId: z.string().nullable(),
  transactionHash: z.string().nullable(),
  proofCount: z.number(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
});

export const RoundWithWinDtoSchema = z.object({
  id: z.string().uuid(),
  externalRoundId: z.string().nullable(),
  userId: z.string().uuid(),
  partnerId: z.string().uuid().nullable(),
  providerId: z.string().uuid().nullable(),
  gameId: z.string().uuid(),
  sessionId: z.string().uuid().nullable(),
  currency: z.enum(['GC', 'SC']),
  isFinished: z.boolean(),
  totalBet: z.number().nullable(),
  totalWin: z.number().nullable(),
  multiplier: z.number().nullable(),
  winId: z.string().nullable(),
  verificationId: z.string().nullable(),
  verification: RoundVerificationSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const ShareTokenDtoSchema = z.object({
  shareToken: z.string(),
  shareUrl: z.string(),
});

const SharedGameSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  alias: z.string(),
  cover: MediaDtoSchema.nullable(),
});

export const SharedRoundDtoSchema = z.object({
  game: SharedGameSummarySchema,
  currency: z.enum(['GC', 'SC']),
  totalBet: z.number().nullable(),
  totalWin: z.number().nullable(),
  multiplier: z.number().nullable(),
  winId: z.string().nullable().optional(),
  verification: RoundVerificationSchema,
  createdAt: z.string(),
});

export const RoundsWithWinsParamsSchema = PaginationParamsSchema();

export const GamesWithWinsFiltersSchema = z.object({
  name: z.string().optional(),
}).optional();

export const GamesWithWinsParamsSchema = PaginationParamsSchema(GamesWithWinsFiltersSchema);
export type GamesWithWinsParams = z.infer<typeof GamesWithWinsParamsSchema>;

export type ShareTokenDto = z.infer<typeof ShareTokenDtoSchema>;
export type SharedRoundDto = z.infer<typeof SharedRoundDtoSchema>;
export type RoundWithWinDto = z.infer<typeof RoundWithWinDtoSchema>;
export type RoundsWithWinsParams = z.infer<typeof RoundsWithWinsParamsSchema>;

export type GameDto = z.infer<typeof GameDtoSchema>;
export type GameListItemDto = z.infer<typeof GameListItemDtoSchema>;
export type GameListDto = PaginationType<GameListItemDto>;
export type GameLaunchUrlPayload = z.infer<typeof GameLaunchUrlPayloadSchema>;
export type GameLaunchUrlResponse = z.infer<typeof GameLaunchUrlResponseSchema>;
export type GameDetailsDto = z.infer<typeof GameDetailsDtoSchema>;
